"use client";
import React, { useState, useRef, useEffect } from "react";
import "./styles.scss";

interface VideoPlayerProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  videoSrc: string;
  skeletonClassName?: string;
  autoPlay?: boolean;
  loop?: boolean;
  displayAudioControl?: boolean;
  muted?: boolean;
  onReplay?: () => void;
  onLoaded?: (loaded: boolean) => void;
  defaultVolume?: number;
}

export default function VideoPlayer({
  className = "",
  width = "100%",
  height = "100%",
  videoSrc,
  skeletonClassName,
  autoPlay = true,
  loop = true,
  displayAudioControl = false,
  muted = true,
  onReplay,
  onLoaded,
  defaultVolume = 1,
}: VideoPlayerProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isHovered, setIsHovered] = useState(false);
  const [volume, setVolume] = useState(defaultVolume);
  const videoRef = useRef<HTMLVideoElement>(null);
  const observerRef = useRef(null);
  const [renderUrl, setRenderUrl] = useState<string>("");

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  };

  useEffect(() => {
    if (onReplay) {
      // 监听 onReplay prop 的变化
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    }
  }, [onReplay]);

  // 添加视频加载状态检查
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // 如果视频已经有数据，直接设置为已加载
      if (video.readyState >= 2) {
        setIsVideoLoaded(true);
        onLoaded?.(true);
      }
    }
  }, [onLoaded]);

  const handleVideoEvents = {
    onLoadStart: () => {
      // console.log("1. 开始加载视频");
    },
    onLoadedMetadata: () => {
      // console.log("2. 视频元数据已加载");
    },
    onLoadedData: () => {
      // console.log("3. 第一帧已加载");
      setIsVideoLoaded(true);
      onLoaded?.(true);
    },
    onCanPlay: () => {
      // console.log("4. 视频可以开始播放");
      setIsVideoLoaded(true);
      onLoaded?.(true);
    },
    onPlaying: () => {
      // console.log("5. 视频开始播放");
      setIsVideoLoaded(true);
      onLoaded?.(true);
    },
    onError: (e: unknown) => {
      console.error(e);
      // 获取具体错误信息
      // if (videoRef.current) {
      //   console.error("错误代码:", videoRef.current.error?.code);
      //   console.error("错误信息:", videoRef.current.error?.message);
      // }
    },
    // 添加更多调试事件
    onStalled: () => {
      // console.log("视频加载停滞");
    },
    onSuspend: () => {
      // console.log("视频加载暂停");
    },
    onAbort: () => {
      // console.log("视频加载中断");
    },
    onWaiting: () => {
      // console.log("视频等待中");
    },
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    setRenderUrl(videoSrc); // 直接设置新的视频源

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (autoPlay) {
            videoRef.current?.play();
          }
        } else {
          if (autoPlay) {
            videoRef.current?.pause();
          } else {
            videoRef.current?.pause();
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(callback, options);

    if (videoRef.current) {
      observerRef.current.observe(videoRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [videoSrc, autoPlay]);

  return (
    <div
      className={`video-container ${className}`}
      style={{ width, height }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      {!isVideoLoaded && <div className={`video-skeleton ${skeletonClassName || ""}`}></div>}
      {displayAudioControl && isHovered && (
        <div className="video-control-wrapper">
          <div onClick={toggleMute} className="video-control-volume-box">
            {isMuted || volume === 0 ? (
              <i className={`icon iconfont icon-volume-mute-fill`}></i>
            ) : (
              <i className={`icon iconfont icon-volume-up-fill`}></i>
            )}
          </div>
          <input
            className="video-control-range-input"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            style={{ "--value": `${volume * 100}%` } as React.CSSProperties}
          />
        </div>
      )}
      <video
        key={videoSrc} // 添加 key 属性强制重新渲染
        ref={videoRef}
        className={`background-video ${isVideoLoaded ? "loaded" : ""}`}
        autoPlay={autoPlay}
        muted={isMuted}
        loop={loop}
        playsInline
        {...handleVideoEvents}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}
